import { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { ArrowLeft, Calendar, Clock, Tag, MessageCircle, Send, Trash2 } from 'lucide-react';
import dayjs from 'dayjs';
import { Markdown } from '../components/Markdown';
import { api } from '../api';
import { useAuthStore } from '../store/auth';
import { CommentView } from '../types';

export function PostPage() {
  const { slug } = useParams<{ slug: string }>();
  const { role } = useAuthStore();
  const queryClient = useQueryClient();
  const [comment, setComment] = useState('');
  const contentRef = useRef<HTMLDivElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);

  const { data: post, isLoading } = useQuery({
    queryKey: ['post', slug],
    queryFn: () => api.getPost(slug!),
  });

  const { data: comments } = useQuery({
    queryKey: ['comments', slug],
    queryFn: () => api.listComments(slug!),
    enabled: !!slug,
  });

  const createComment = useMutation({
    mutationFn: () => api.createComment(slug!, { content: comment }),
    onSuccess: () => {
      setComment('');
      queryClient.invalidateQueries({ queryKey: ['comments', slug] });
    },
  });

  const deleteComment = useMutation({
    mutationFn: (id: string) => api.deleteComment(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['comments', slug] }),
  });

  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.querySelectorAll('pre').forEach((pre) => {
        if (!pre.querySelector('button.copy-btn')) {
          const btn = document.createElement('button');
          btn.className = 'copy-btn absolute right-2 top-2 rounded bg-slate-700/80 px-2 py-1 text-xs text-white opacity-0 transition-opacity hover:bg-slate-600';
          btn.textContent = '复制';
          btn.onclick = () => {
            const code = pre.querySelector('code')?.textContent || '';
            navigator.clipboard.writeText(code);
            btn.textContent = '已复制';
            setTimeout(() => (btn.textContent = '复制'), 1500);
          };
          pre.classList.add('relative');
          pre.appendChild(btn);
          pre.onmouseenter = () => (btn.style.opacity = '1');
          pre.onmouseleave = () => (btn.style.opacity = '0');
        }
      });
    }
  }, [post?.content_md]);

  useEffect(() => {
    const handleScroll = () => {
      if (!progressRef.current) return;
      const winScroll = document.documentElement.scrollTop;
      const height = document.documentElement.scrollHeight - document.documentElement.clientHeight;
      const scrolled = height > 0 ? winScroll / height : 0;
      progressRef.current.style.transform = `scaleX(${scrolled})`;
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (isLoading) return <PostSkeleton />;
  if (!post) return <div className="pt-32 text-center text-slate-500">文章不存在</div>;

  const readTime = Math.max(1, Math.ceil(post.content_md.replace(/[#*`_\[\]\(\)\n]/g, '').length / 400));

  return (
    <>
      <div ref={progressRef} className="progress-bar" />
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="mx-auto max-w-3xl px-4 pb-20 pt-24 sm:px-6 lg:px-8"
      >
        <Link
          to="/"
          className="mb-8 inline-flex items-center gap-2 text-sm text-slate-500 hover:text-indigo-600 dark:text-star-dim dark:hover:text-nebula-glow"
        >
          <ArrowLeft size={16} /> 返回首页
        </Link>

        <header className="mb-10">
          <h1 className="mb-6 text-3xl font-black leading-tight text-slate-900 dark:text-star sm:text-5xl">
            {post.title}
          </h1>
          <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500 dark:text-star-dim">
            <div className="flex items-center gap-1">
              <Calendar size={14} />
              {post.published_at ? dayjs(post.published_at).format('YYYY-MM-DD') : '未发布'}
            </div>
            <div className="flex items-center gap-1">
              <Clock size={14} />
              {readTime} 分钟阅读
            </div>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            {post.tags.map((tag) => (
              <Link
                key={tag}
                to={`/?tag=${encodeURIComponent(tag)}`}
                className="tag-pill bg-slate-100 text-slate-600 transition-colors hover:bg-indigo-100 hover:text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300 dark:hover:bg-indigo-500/20"
              >
                <Tag size={10} /> {tag}
              </Link>
            ))}
          </div>
        </header>

        <article
          ref={contentRef}
          className="prose prose-lg max-w-none dark:prose-invert prose-headings:scroll-mt-24 prose-a:text-indigo-600 dark:prose-a:text-nebula-glow"
        >
          <Markdown content={post.content_md} />
        </article>

        <section className="mt-16">
          <h3 className="mb-6 flex items-center gap-2 text-xl font-bold text-slate-900 dark:text-star">
            <MessageCircle size={20} /> 评论 ({comments?.length || 0})
          </h3>

          {role !== 'guest' ? (
            <div className="mb-8">
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="写下你的想法..."
                rows={3}
                className="input-glow w-full rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-900 outline-none transition-all focus:border-indigo-500 dark:border-indigo-500/20 dark:bg-space-800 dark:text-star"
              />
              <div className="mt-2 flex justify-end">
                <button
                  onClick={() => createComment.mutate()}
                  disabled={!comment.trim() || createComment.isPending}
                  className="btn-press inline-flex items-center gap-2 rounded-full bg-indigo-600 px-5 py-2 text-sm font-medium text-white transition-all hover:bg-indigo-700 disabled:opacity-50 dark:bg-nebula dark:hover:bg-indigo-500"
                >
                  <Send size={14} /> 发表评论
                </button>
              </div>
            </div>
          ) : (
            <p className="mb-8 text-sm text-slate-500 dark:text-star-dim">
              登录后可发表评论。
            </p>
          )}

          <div className="space-y-4">
            {comments?.map((c) => (
              <CommentItem
                key={c.id}
                comment={c}
                canDelete={role === 'admin'}
                onDelete={() => deleteComment.mutate(c.id)}
              />
            ))}
          </div>
        </section>
      </motion.div>
    </>
  );
}

function CommentItem({
  comment,
  canDelete,
  onDelete,
}: {
  comment: CommentView;
  canDelete: boolean;
  onDelete: () => void;
}) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 dark:border-indigo-500/10 dark:bg-space-800/60">
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {comment.author.avatar_url ? (
            <img src={comment.author.avatar_url} alt="" className="h-6 w-6 rounded-full" />
          ) : (
            <div className="flex h-6 w-6 items-center justify-center rounded-full bg-indigo-100 text-xs font-bold text-indigo-600 dark:bg-indigo-500/20 dark:text-indigo-300">
              {comment.author.name[0]}
            </div>
          )}
          <span className="text-sm font-medium text-slate-900 dark:text-star">{comment.author.name}</span>
          <span className="text-xs text-slate-400">{dayjs(comment.created_at).format('YYYY-MM-DD HH:mm')}</span>
        </div>
        {canDelete && (
          <button onClick={onDelete} className="text-slate-400 hover:text-red-500">
            <Trash2 size={14} />
          </button>
        )}
      </div>
      <p className="whitespace-pre-wrap text-sm text-slate-700 dark:text-star-dim">{comment.content}</p>
    </div>
  );
}

function PostSkeleton() {
  return (
    <div className="mx-auto max-w-3xl px-4 pb-20 pt-24">
      <div className="mb-6 h-12 animate-pulse rounded bg-slate-200 dark:bg-space-800"></div>
      <div className="mb-4 h-4 w-1/3 animate-pulse rounded bg-slate-200 dark:bg-space-800"></div>
      <div className="space-y-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="h-24 animate-pulse rounded bg-slate-200 dark:bg-space-800"></div>
        ))}
      </div>
    </div>
  );
}
